import { forwardRef, useMemo, useRef, useEffect } from 'react';

/**
 * Un titular cuyas letras cambian de peso según qué tan cerca pasa el cursor.
 *
 * Vendorizado de React Bits (`VariableProximity`, variante JS+Tailwind). Como
 * los bloques de `components/magicui/`, es código de terceros con nombre en
 * inglés: se deja lo más parecido posible al original para poder compararlo
 * contra la fuente el día que haya que actualizarlo.
 *
 *
 * POR QUÉ ESTÁ ACÁ Y NO ES DECORACIÓN
 *
 * La landing vieja tenía el titular con cuatro adjetivos que rotaban solos.
 * Eso retrasa lo único que tiene que llegar instantáneo —la propuesta de valor,
 * que según el eyetracking de NN/g se juega arriba del pliegue y en los
 * primeros segundos— y encima es el efecto que trae cualquier plantilla.
 *
 * Esto es lo contrario: el texto está completo y quieto desde el primer frame,
 * y lo que responde es el TRAZO. No hay nada que esperar para leerlo, y el
 * movimiento sólo existe si el visitante mueve el mouse. En teléfono no hay
 * cursor, así que no pasa nada y el titular se lee tal cual: la degradación es
 * el comportamiento correcto, no una carencia.
 *
 *
 * LOS TRES CAMBIOS CONTRA EL ORIGINAL
 *
 * 1. **Se le sacó la fuente hardcodeada.** El original trae
 *    `fontFamily: '"Roboto Flex", sans-serif'` en un estilo inline, o sea que
 *    pisaba cualquier tipografía que le pusiéramos por clase. Nuestra fuente es
 *    Bricolage Grotesque (`font-titular`), así que el efecto se veía con la
 *    fuente equivocada o directamente no se veía. Ahora la fuente la decide
 *    quien lo monta.
 *
 * 2. **`motion.span` → `span`.** El original envuelve CADA letra en un
 *    `motion.span` sin pasarle una sola prop de animación: los pesos se
 *    escriben a mano sobre `style.fontVariationSettings` en el bucle de abajo.
 *    Eran ~40 componentes de framer por titular sin que framer hiciera nada.
 *    Con esto el archivo además deja de depender de `motion`.
 *
 * 3. **El bucle se apaga cuando el puntero no está encima.** El original deja
 *    un `requestAnimationFrame` corriendo para siempre, aunque nadie mueva el
 *    mouse y aunque el titular esté fuera de pantalla. Ahora arranca al entrar
 *    el puntero y se detiene al salir, que es exactamente cuando el efecto
 *    puede pasar algo.
 *
 *
 * LA FUENTE TIENE QUE SER VARIABLE. Si el navegador bajó una fuente estática,
 * `font-variation-settings` no tiene qué interpolar y no pasa nada. Por eso el
 * `@import` de index.css pide los ejes con la sintaxis `wght@200..800` y no una
 * lista de pesos sueltos.
 *
 * @param label      El texto. Va completo en un `<span class="sr-only">`, así
 *                   que un lector de pantalla lee una frase y no 40 letras.
 * @param fromFontVariationSettings  El trazo en reposo, ej. `"'wght' 400"`.
 * @param toFontVariationSettings    El trazo bajo el cursor, ej. `"'wght' 800"`.
 * @param containerRef  Contra qué caja se mide la distancia. Es el elemento que
 *                      además escucha la entrada y salida del puntero.
 * @param radius     Radio de influencia en px.
 * @param falloff    'linear' | 'exponential' | 'gaussian'.
 */
const VariableProximity = forwardRef((props, ref) => {
  const {
    label,
    fromFontVariationSettings,
    toFontVariationSettings,
    containerRef,
    radius = 50,
    falloff = 'linear',
    className = '',
    onClick,
    style,
    ...restProps
  } = props;

  const letterRefs = useRef([]);
  const posicionDelPuntero = useRef({ x: 0, y: 0 });
  const ultimaPosicion = useRef({ x: null, y: null });

  const parsedSettings = useMemo(() => {
    const parsear = (texto) =>
      new Map(
        texto
          .split(',')
          .map((s) => s.trim())
          .map((s) => {
            const [name, value] = s.split(' ');
            return [name.replace(/['"]/g, ''), parseFloat(value)];
          })
      );

    const desde = parsear(fromFontVariationSettings);
    const hasta = parsear(toFontVariationSettings);

    return Array.from(desde.entries()).map(([axis, fromValue]) => ({
      axis,
      fromValue,
      toValue: hasta.get(axis) ?? fromValue,
    }));
  }, [fromFontVariationSettings, toFontVariationSettings]);

  useEffect(() => {
    const caja = containerRef?.current;
    if (!caja) return undefined;

    let frameId = null;
    let activo = false;

    const distancia = (x1, y1, x2, y2) => Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

    const atenuar = (d) => {
      const norm = Math.min(Math.max(1 - d / radius, 0), 1);
      if (falloff === 'exponential') return norm ** 2;
      if (falloff === 'gaussian') return Math.exp(-((d / (radius / 2)) ** 2) / 2);
      return norm;
    };

    /** Devuelve todas las letras a su trazo de reposo. */
    const reposar = () => {
      letterRefs.current.forEach((letra) => {
        if (letra) letra.style.fontVariationSettings = fromFontVariationSettings;
      });
    };

    const pintar = () => {
      const { x, y } = posicionDelPuntero.current;
      // Nada se movió: no hay nada que recalcular.
      if (ultimaPosicion.current.x === x && ultimaPosicion.current.y === y) return;
      ultimaPosicion.current = { x, y };

      const cajaRect = caja.getBoundingClientRect();

      letterRefs.current.forEach((letra) => {
        if (!letra) return;

        const rect = letra.getBoundingClientRect();
        const centroX = rect.left + rect.width / 2 - cajaRect.left;
        const centroY = rect.top + rect.height / 2 - cajaRect.top;
        const d = distancia(x, y, centroX, centroY);

        if (d >= radius) {
          letra.style.fontVariationSettings = fromFontVariationSettings;
          return;
        }

        const factor = atenuar(d);
        letra.style.fontVariationSettings = parsedSettings
          .map(({ axis, fromValue, toValue }) => `'${axis}' ${fromValue + (toValue - fromValue) * factor}`)
          .join(', ');
      });
    };

    const bucle = () => {
      pintar();
      frameId = requestAnimationFrame(bucle);
    };

    const anotarPosicion = (clientX, clientY) => {
      const rect = caja.getBoundingClientRect();
      posicionDelPuntero.current = { x: clientX - rect.left, y: clientY - rect.top };
    };

    const alMover = (ev) => anotarPosicion(ev.clientX, ev.clientY);
    const alTocar = (ev) => {
      const dedo = ev.touches[0];
      if (dedo) anotarPosicion(dedo.clientX, dedo.clientY);
    };

    const arrancar = () => {
      if (activo) return;
      activo = true;
      frameId = requestAnimationFrame(bucle);
    };

    const parar = () => {
      activo = false;
      if (frameId) cancelAnimationFrame(frameId);
      frameId = null;
      ultimaPosicion.current = { x: null, y: null };
      reposar();
    };

    caja.addEventListener('pointerenter', arrancar);
    caja.addEventListener('pointerleave', parar);
    window.addEventListener('mousemove', alMover);
    window.addEventListener('touchmove', alTocar, { passive: true });

    reposar();

    return () => {
      caja.removeEventListener('pointerenter', arrancar);
      caja.removeEventListener('pointerleave', parar);
      window.removeEventListener('mousemove', alMover);
      window.removeEventListener('touchmove', alTocar);
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [containerRef, radius, falloff, parsedSettings, fromFontVariationSettings]);

  const palabras = label.split(' ');
  let indice = 0;

  return (
    <span ref={ref} onClick={onClick} style={style} className={className} {...restProps}>
      {palabras.map((palabra, i) => (
        <span key={i} className="inline-block whitespace-nowrap">
          {palabra.split('').map((letra) => {
            const actual = indice++;
            return (
              <span
                key={actual}
                ref={(el) => {
                  letterRefs.current[actual] = el;
                }}
                style={{ display: 'inline-block' }}
                aria-hidden="true"
              >
                {letra}
              </span>
            );
          })}
          {i < palabras.length - 1 && <span className="inline-block">&nbsp;</span>}
        </span>
      ))}
      {/* El texto de verdad, para lectores de pantalla y para que el navegador
          lo encuentre al buscar en la página. Las letras de arriba son dibujo. */}
      <span className="sr-only">{label}</span>
    </span>
  );
});

VariableProximity.displayName = 'VariableProximity';
export default VariableProximity;
